'use client';
import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { imagePathResolver, NumberFormats as nf, ROUTES, useClientConfig, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { BigWinnerApiData, GamesApiResponse } from '../../../types/types';

type BigWinnerData = {
  id: string;
  amount: string;
  multiplier: string;
  playerName: string;
  href: string;
  logo: string | null;
  alt: string;
};

/**
 * useBigWinners hook fetches big winners data from the API and maps it to the BigWinnerData type.
 * It also fetches games data from the API and maps game logo from it.
 * If there is API error, hook will return empty array and UI will not render anything.
 * Also, if game is not found in games api, logo will be null.
 * @returns An object containing the mapped data and the loading state.
 */
function useBigWinners(): { data: BigWinnerData[]; isLoading: boolean } {
  const { apiLocale, currency, baseImgUrl } = useClientConfig();
  const { data, isLoading } = useApiQuery<BigWinnerApiData[]>(API_ROUTES.GAMES.BIG_WINNERS.replace(':currency', currency));
  const { data: games } = useApiQuery<GamesApiResponse>(API_ROUTES.GAMES.GAMES_LIST.replace(':locale', apiLocale).replace(':currency', currency));
  const t = useTranslations('casino.discover_page.big_winners');
  const currencyFormatter = useCurrencyFormatter();

  const gameLogosByCode = new Map(games?.games?.map(game => [game.name, game.logo]));

  const mappedData =
    data && data.length
      ? data.map(({ id, winningAmount, multiplier, playerName, gameCode }) => ({
          id,
          amount: currencyFormatter(winningAmount)
            .format(nf.MIXED_DECIMALS, value => value)
            .get(),
          multiplier: t('multiplier', { multiplier }),
          playerName,
          href: ROUTES.CASINO.GAME(gameCode),
          logo: gameLogosByCode.get(gameCode) ? imagePathResolver(gameLogosByCode.get(gameCode) ?? '', baseImgUrl, true) : null,
          alt: gameCode,
        }))
      : [];

  return { data: mappedData, isLoading };
}

export { useBigWinners };
